/**
 * PortalAuditLog — Chronological log of session events
 *
 * Pulls session_events as a proxy audit log with time range filtering.
 */

import React, { useState, useEffect, useMemo } from 'react';
import PortalLayout from '@/components/portal/PortalLayout';
import { supabaseGet } from '@/utils/supabaseRest';
import { ScrollText, Loader2, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';

// ─── Types ───────────────────────────────────────

interface AuditEvent {
  id: string;
  session_id: string;
  event_type: string;
  vertical_id: string | null;
  phase: number | null;
  score: number | null;
  revenue: number | null;
  created_at: string;
}

interface VerticalRef {
  id: string;
  name: string;
}

// ─── Component ───────────────────────────────────

const TIME_RANGES = [
  { label: '7 days', days: 7 },
  { label: '30 days', days: 30 },
  { label: '90 days', days: 90 },
] as const;

export default function PortalAuditLog() {
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [verticals, setVerticals] = useState<VerticalRef[]>([]);
  const [loading, setLoading] = useState(true);
  const [rangeDays, setRangeDays] = useState<number>(30);

  const verticalMap = useMemo(() => {
    const m: Record<string, string> = {};
    verticals.forEach(v => { m[v.id] = v.name; });
    return m;
  }, [verticals]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const since = new Date(Date.now() - rangeDays * 86400000).toISOString();
        const [evts, verts] = await Promise.all([
          supabaseGet<AuditEvent[]>(
            `session_events?select=id,session_id,event_type,vertical_id,phase,score,revenue,created_at&order=created_at.desc&limit=200&created_at=gte.${since}`
          ),
          supabaseGet<VerticalRef[]>('verticals?select=id,name'),
        ]);
        setEvents(evts);
        setVerticals(verts);
      } catch (e) {
        console.error('Failed to load audit log:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [rangeDays]);

  const eventColor = (type: string): string => {
    switch (type) {
      case 'converted': return 'text-emerald-400';
      case 'rejected': return 'text-red-400';
      case 'presented': return 'text-blue-400';
      case 'session_end': return 'text-amber-400';
      default: return 'text-slate-400';
    }
  };

  return (
    <PortalLayout>
      <div className="p-6 max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ScrollText className="h-6 w-6 text-emerald-400" />
            <h1 className="text-2xl font-bold">Audit Log</h1>
          </div>

          {/* Time range filter */}
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-slate-500" />
            {TIME_RANGES.map(r => (
              <Button
                key={r.days}
                variant="ghost"
                size="sm"
                onClick={() => setRangeDays(r.days)}
                className={rangeDays === r.days
                  ? 'bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/15'
                  : 'text-slate-400 hover:text-white'
                }
              >
                {r.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-6 w-6 animate-spin text-emerald-400" />
              <span className="ml-2 text-slate-400">Loading audit log...</span>
            </div>
          ) : events.length === 0 ? (
            <div className="text-center py-20 text-slate-500">
              <ScrollText className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No events found in the selected time range.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-800 text-left">
                    <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Timestamp</th>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Action</th>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Vertical</th>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Phase</th>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Score</th>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Revenue</th>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Session ID</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {events.map(e => (
                    <tr key={e.id} className="hover:bg-slate-800/50 transition-colors">
                      <td className="px-4 py-3 text-slate-300 whitespace-nowrap">
                        {new Date(e.created_at).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`font-medium ${eventColor(e.event_type)}`}>
                          {e.event_type}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-300 whitespace-nowrap">
                        {e.vertical_id ? (verticalMap[e.vertical_id] || e.vertical_id.slice(0, 8)) : '—'}
                      </td>
                      <td className="px-4 py-3 text-slate-300">
                        {e.phase != null ? e.phase : '—'}
                      </td>
                      <td className="px-4 py-3 text-slate-300">
                        {e.score != null ? e.score.toFixed(1) : '—'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {e.revenue != null ? (
                          <span className="text-emerald-400 font-medium">${e.revenue.toFixed(2)}</span>
                        ) : '—'}
                      </td>
                      <td className="px-4 py-3 text-slate-500 font-mono text-xs whitespace-nowrap">
                        {e.session_id.slice(0, 12)}...
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <p className="text-xs text-slate-600 text-right">
          Showing {events.length} events from the last {rangeDays} days (max 200)
        </p>
      </div>
    </PortalLayout>
  );
}
